import { Component, DoCheck, Input, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { TeamMemberAccessService } from 'src/app/api/team-member-access.service';
import { TodosService, TODO_COMMUNICATION_TYPE_TAGS } from 'src/app/api/todos.service';
import { AddressBookComponent } from '../../address-book/address-book.component';
import { SelectComponent as TagsSelectPage } from '../../tags/select/select.component';
import { TaskStatus } from '../../tasks/TaskStatus';

@Component({
  selector: 'app-todo-edit',
  templateUrl: './todo-edit.component.html',
  styleUrls: ['./todo-edit.component.scss'],
})
export class TodoEditComponent implements OnInit, DoCheck {
  // Pass an existing To Do to edit it; leave null to create a new one
  @Input() todo: any = null;
  @Input() default_assigned_guid = '';
  // A new To Do opened from a contact's / company's To Do tab starts linked to it
  @Input() default_client_guid = '';
  @Input() default_company_guid = '';
  @Input() default_linked_name = '';

  item: any = null;
  is_new = true;
  saving = false;
  team_members: any[] = [];
  communication_types: any[] = [];
  current_user: any = null;

  // datetime-local input bindings
  received_date_local = '';
  due_date_local = '';
  completed_date_local = '';

  // Client / company quick search (same behaviour as the missing info modal)
  client_data: any[] = [];
  company_data: any[] = [];
  combined_data: any[] = [];
  client_search_query = '';
  client_suggestions: any[] = [];
  client_suggestions_active_index = -1;
  show_client_suggestions = false;
  client_search_blurred_with_text = false;

  // Communication type quick search (same behaviour as the tag search on clients)
  // Read-only (completed) - the Re-open button in the footer unlocks it
  locked = false;
  reopening = false;
  private reopened = false;
  new_checklist_item = '';
  new_checklist_description = '';
  comm_search_query = '';
  comm_suggestions: any[] = [];
  comm_suggestions_active_index = -1;
  show_comm_suggestions = false;

  private original_assigned_guid = '';
  private original_snapshot = '';

  constructor(public modalController: ModalController, public globalService: GlobalService, public todosService: TodosService,
    private apiService: ApiService, private alertController: AlertController, private teamMemberAccessService: TeamMemberAccessService) { }

  async ngOnInit() {
    this.current_user = this.globalService.GetCurrentUser();
    this.is_new = !this.todo?.guid;
    this.item = this.is_new ? this.todosService.NewTodo() : JSON.parse(JSON.stringify(this.todo));
    if (!this.item.communication_types) this.item.communication_types = [];
    if (!Array.isArray(this.item.checklist)) this.item.checklist = [];
    // A To Do that is already completed when opened is read-only until it is re-opened
    this.locked = !this.is_new && this.todosService.IsCompleted(this.item);
    if (this.is_new && this.default_assigned_guid) this.item.assigned_member_guid = this.default_assigned_guid;
    if (this.is_new && (this.default_client_guid || this.default_company_guid)) {
      this.item.client_guid = this.default_client_guid || '';
      this.item.company_guid = this.default_company_guid || '';
      this.item.linked_name = this.default_linked_name || '';
    }
    this.original_assigned_guid = this.item.assigned_member_guid || '';

    this.received_date_local = this.todosService.ToLocalInput(this.item.received_date);
    this.due_date_local = this.todosService.ToLocalInput(this.item.due_date);
    this.completed_date_local = this.todosService.ToLocalInput(this.item.completed_date);
    this.original_snapshot = this.Snapshot();

    [this.team_members, this.communication_types] = await Promise.all([
      this.todosService.GetTeamMembers(),
      this.todosService.GetCommunicationTypes(),
    ]);
    this.GetCompaniesAndClients();
  }

  GetMemberName(guid: string): string {
    const member = this.team_members.find(m => m.guid === guid);
    return member ? member.display_name : '';
  }

  // ── Warning when the To Do is for a team member who can't see its contact / company ──
  // Checked for both the team member assigned and the team member who received it, whenever either or the linked contact /
  // company changes. It only warns - saving is never blocked.
  access_warning = '';          // about the team member assigned
  received_access_warning = ''; // about the team member received by
  private access_check_key = '';

  ngDoCheck() {
    if (!this.item) return;
    // The team member list is part of the key so the check runs again once it has loaded (it holds who is an admin)
    const key = `${this.item.assigned_member_guid || ''}|${this.item.received_by_guid || ''}|${this.item.client_guid || ''}|${this.item.company_guid || ''}|${this.team_members.length}`;
    if (key === this.access_check_key) return;
    this.access_check_key = key;
    this.CheckMembersAccess(key);
  }

  private async CheckMembersAccess(key: string) {
    this.access_warning = '';
    this.received_access_warning = '';
    const assigned_guid = this.item.assigned_member_guid;
    const received_guid = this.item.received_by_guid;
    // The same person in both fields gets the warning under both - checked once and shown twice
    const same_person = !!received_guid && received_guid === assigned_guid;
    const [assigned_warning, received_result] = await Promise.all([
      this.AccessWarningFor(assigned_guid),
      same_person ? Promise.resolve(null) : this.AccessWarningFor(received_guid),
    ]);
    const received_warning = same_person ? assigned_warning : (received_result || '');
    // Something else changed while this was loading - that change has its own check
    if (key !== this.access_check_key) return;
    this.access_warning = assigned_warning;
    this.received_access_warning = received_warning;
  }

  // The warning text for a team member who can't see the linked contact / company, or '' if they can (or it can't be checked)
  private async AccessWarningFor(member_guid: string): Promise<string> {
    if (!member_guid || member_guid === this.current_user?.guid) return '';
    if (!this.item.client_guid && !this.item.company_guid) return '';

    const member = this.team_members.find(m => m.guid === member_guid);
    if (!member) return ''; // the team member list hasn't loaded yet - the check runs again when it has
    const result = await this.teamMemberAccessService.Check(member, this.item.client_guid, this.item.company_guid);
    if (!result || result.has_access) return '';
    const who = member.display_name || 'This team member';
    const client = this.item.linked_name || 'this contact / company';
    return `${who} doesn't have access to ${client}, so they won't be able to see this To Do.`;
  }

  SetDueQuick(option: 'hour' | 'eod' | 'tomorrow') {
    let due = dayjs();
    if (option === 'hour') due = due.add(1, 'hour').startOf('minute');
    else if (option === 'eod') due = due.hour(17).minute(0);
    else due = due.add(1, 'day').hour(9).minute(0);
    this.due_date_local = due.format('YYYY-MM-DDTHH:mm');
  }

  get is_completed(): boolean {
    return this.todosService.IsCompleted(this.item);
  }

  set is_completed(value: boolean) {
    if (this.locked) return;
    this.item.status = value ? TaskStatus.completed : TaskStatus.active;
    if (value) {
      this.completed_date_local = dayjs().format('YYYY-MM-DDTHH:mm');
      this.item.completed_by_guid = this.current_user.guid;
    } else {
      this.completed_date_local = '';
      this.item.completed_by_guid = null;
    }
  }

  // Plain-English list of what's still missing, shown in the footer like the missing info modal
  GetMissingRequiredFields(): string[] {
    const missing: string[] = [];
    if (!this.item?.title?.trim()) missing.push('To Do Item Name');
    if (!this.received_date_local) missing.push('Received Date/Time');
    if (!this.item?.assigned_member_guid) missing.push('Team Member Assigned');
    return missing;
  }

  GetSaveBlockedMessage(): string {
    const missing = this.GetMissingRequiredFields();
    if (missing.length) return `Please fill out: ${missing.join(', ')}.`;
    if (this.client_search_query?.trim()) return 'Select a suggested client, or clear the client search.';
    return '';
  }

  // ── Client link ───────────────────────────────────────────────────────────

  async GetCompaniesAndClients() {
    const [clients, companies]: any[] = await Promise.all([this.apiService.GetClients(), this.apiService.GetCompanies()]);
    this.client_data = (clients?.data || []).map((client: any) => ({ ...client, is_company: false }));
    this.company_data = (companies?.data || []).map((company: any) => ({ ...company, is_company: true }));
    this.combined_data = [...this.client_data, ...this.company_data];
  }

  HasLinkedClient(): boolean {
    return !!(this.item.client_guid || this.item.company_guid);
  }

  OnClientSearchChange() {
    this.client_search_blurred_with_text = false;
    const q = (this.client_search_query || '').trim().toLowerCase();
    this.client_suggestions_active_index = -1;
    if (!q || !this.combined_data.length) {
      this.client_suggestions = [];
      this.show_client_suggestions = false;
      return;
    }
    this.client_suggestions = this.combined_data.filter((item: any) => item.is_company
      ? (item.name || '').toLowerCase().includes(q)
      : (item.display_name || '').toLowerCase().includes(q) || (item.email || '').toLowerCase().includes(q)
    ).slice(0, 15);
    this.show_client_suggestions = this.client_suggestions.length > 0;
  }

  OnClientInputFocus() {
    this.client_search_blurred_with_text = false;
    if (this.client_search_query) this.OnClientSearchChange();
  }

  OnClientInputBlur() {
    this.show_client_suggestions = false;
    this.client_search_blurred_with_text = !!this.client_search_query?.trim();
  }

  OnClientInputKeydown(event: KeyboardEvent) {
    if (!this.show_client_suggestions || this.client_suggestions.length === 0) {
      if (event.key === 'Enter' && this.client_search_query?.trim()) {
        event.preventDefault();
        this.client_search_blurred_with_text = true;
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.client_suggestions_active_index = Math.min(this.client_suggestions_active_index + 1, this.client_suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.client_suggestions_active_index = Math.max(this.client_suggestions_active_index - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.client_suggestions_active_index >= 0 ? this.client_suggestions_active_index : 0;
      this.SelectClient(this.client_suggestions[index]);
    } else if (event.key === 'Escape') {
      this.show_client_suggestions = false;
    }
  }

  SelectClient(item: any) {
    this.item.client_guid = item && !item.is_company ? item.guid : '';
    this.item.company_guid = item?.is_company ? item.guid : '';
    this.item.linked_name = item ? (item.is_company ? item.name : item.display_name) : '';
    this.client_search_query = '';
    this.client_suggestions = [];
    this.client_suggestions_active_index = -1;
    this.show_client_suggestions = false;
    this.client_search_blurred_with_text = false;
  }

  RemoveClient() {
    this.SelectClient(null);
  }

  async PresentAddressBookModal() {
    const modal = await this.modalController.create({
      component: AddressBookComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_client_selection: true,
        allow_non_client_selection: false,
        allow_company_selection: true,
        allow_contact_selection: true,
        allow_company_group_selection: false,
        allow_contact_group_selection: false,
        expanded_clients: true,
        in_selected_companies: this.company_data.filter(c => c.guid === this.item.company_guid),
        in_selected_contacts: this.client_data.filter(c => c.guid === this.item.client_guid),
        max_allowed_selections: 1
      }
    });
    modal.onDidDismiss().then(async (result: any) => {
      // A contact or company may have been created inside the address book
      await this.GetCompaniesAndClients();
      if (!result?.data) return;
      const selected = result.data[0];
      if (!selected) this.SelectClient(null);
      else if (selected.is_company) this.SelectClient({ ...selected, is_company: true });
      else if (selected.is_contact) this.SelectClient({ ...selected, is_company: false });
    });
    await modal.present();
  }

  // ── Communication types (tags) ────────────────────────────────────────────

  OnCommSearchChange() {
    const q = (this.comm_search_query || '').trim().toLowerCase();
    this.comm_suggestions_active_index = -1;
    if (!q || !this.communication_types.length) {
      this.comm_suggestions = [];
      this.show_comm_suggestions = false;
      return;
    }
    const selectedGuids = new Set(this.item.communication_types.map((t: any) => t.guid));
    this.comm_suggestions = this.communication_types
      .filter((t: any) => !selectedGuids.has(t.guid) && (t.title || '').toLowerCase().includes(q))
      .slice(0, 15);
    this.show_comm_suggestions = this.comm_suggestions.length > 0;
  }

  OnCommInputFocus() {
    if (this.comm_search_query) this.OnCommSearchChange();
  }

  OnCommInputBlur() {
    this.show_comm_suggestions = false;
  }

  OnCommInputKeydown(event: KeyboardEvent) {
    if (!this.show_comm_suggestions || this.comm_suggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.comm_suggestions_active_index = Math.min(this.comm_suggestions_active_index + 1, this.comm_suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.comm_suggestions_active_index = Math.max(this.comm_suggestions_active_index - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.comm_suggestions_active_index >= 0 ? this.comm_suggestions_active_index : 0;
      this.SelectCommType(this.comm_suggestions[index]);
    } else if (event.key === 'Escape') {
      this.show_comm_suggestions = false;
    }
  }

  SelectCommType(type: any) {
    if (this.locked) return;
    if (type?.guid && !this.item.communication_types.some((t: any) => t.guid === type.guid)) {
      // Only one communication type is allowed for now, so picking another replaces it
      this.item.communication_types = [type];
    }
    this.comm_search_query = '';
    this.comm_suggestions = [];
    this.comm_suggestions_active_index = -1;
    this.show_comm_suggestions = false;
  }

  RemoveCommType(type: any) {
    if (this.locked) return;
    this.item.communication_types = this.item.communication_types.filter((t: any) => t.guid !== type.guid);
  }

  async PresentSelectCommTypesModal() {
    if (this.locked) return;
    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: TODO_COMMUNICATION_TYPE_TAGS,
        title: 'Select Communication Type (max 1)',
        source_header: 'Available Communication Types',
        target_header: 'Assigned Communication Types',
        name: 'Communication Type',
        local_only: this.todosService.is_local,
        current_tags: [...this.communication_types],
        selected_tags: [...this.item.communication_types],
      }
    });
    modal.onDidDismiss().then(async (result: any) => {
      if (result.data) {
        this.todosService.AddLocalCommunicationTypes(result.data);
        // Only one communication type is allowed for now - keep the newly picked one
        const previous = new Set(this.item.communication_types.map((t: any) => t.guid));
        const picked = result.data.find((t: any) => !previous.has(t.guid)) || result.data[0];
        this.item.communication_types = picked ? [picked] : [];
      }
      // A type may have been created in the picker - reload the list the search box works from
      this.communication_types = await this.todosService.GetCommunicationTypes();
    });
    return await modal.present();
  }

  // ── Checklist ─────────────────────────────────────────────────────────────

  AddChecklistItem() {
    if (this.locked) return;
    const text = (this.new_checklist_item || '').trim();
    if (!text) return;
    const description = (this.new_checklist_description || '').trim();
    const item: any = { guid: this.NewChecklistGuid(), text, checked: false };
    if (description) item.description = description;
    this.item.checklist = [...this.item.checklist, item];
    this.new_checklist_description = '';
    this.new_checklist_item = '';
  }

  RemoveChecklistItem(item: any) {
    if (this.locked) return;
    this.item.checklist = this.item.checklist.filter((i: any) => i.guid !== item.guid);
  }

  ToggleChecklistItem(item: any) {
    if (this.locked) return;
    if (item.checked) {
      item.checked_date = dayjs().toISOString();
      item.checked_by = this.current_user?.display_name || '';
      // Last item ticked - offer to complete the To Do too
      if (!this.is_completed && this.item.checklist.length > 0 && this.item.checklist.every((i: any) => i.checked)) this.OfferCompleteTodo();
    } else {
      item.checked_date = null;
      item.checked_by = null;
    }
  }

  // Completing here only fills in the completed fields - it is saved with the rest of the form
  private async OfferCompleteTodo() {
    const alert = await this.alertController.create({
      header: 'Checklist Complete',
      message: 'Everything on this checklist is checked off. Would you like to mark this To Do as completed?',
      buttons: [
        { text: 'Not Yet', role: 'cancel' },
        { text: 'Mark Completed', handler: () => { this.is_completed = true; } }
      ]
    });
    await alert.present();
  }

  ChecklistCheckedCount(): number {
    return this.item.checklist.filter((i: any) => i.checked).length;
  }

  ChecklistPercent(): number {
    const total = this.item.checklist.length;
    return total === 0 ? 0 : Math.round((this.ChecklistCheckedCount() / total) * 100);
  }

  private NewChecklistGuid(): string {
    return (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  // ── Save / close ──────────────────────────────────────────────────────────

  async Save() {
    if (this.locked) return;
    if (this.GetSaveBlockedMessage() || this.saving) return;

    this.item.title = this.item.title.trim();
    // An item typed but not yet added with Enter / Add is still saved
    this.AddChecklistItem();
    this.item.received_date = this.todosService.FromLocalInput(this.received_date_local);
    this.item.due_date = this.todosService.FromLocalInput(this.due_date_local);
    this.item.completed_date = this.is_completed ? this.todosService.FromLocalInput(this.completed_date_local) : null;

    this.saving = true;
    const data: any = this.is_new
      ? await this.todosService.CreateTodo(this.item)
      : await this.todosService.UpdateTodo(this.item, this.original_assigned_guid);
    this.saving = false;

    this.globalService.presentToastMessage(data.success ? (this.is_new ? 'To Do created' : 'To Do updated') : data.message, data.success);
    if (data.success) this.modalController.dismiss({ saved: true, todo: data.data?.[0] || this.item });
  }

  // Saves straight away (like the completed tick in the list) and unlocks the form for editing
  async Reopen() {
    if (this.reopening) return;
    this.reopening = true;
    const data: any = await this.todosService.SetCompleted(this.item, false);
    this.reopening = false;
    if (!data?.success) {
      this.globalService.presentToastMessage(data?.message || 'Failed to re-open the To Do', false);
      return;
    }
    this.locked = false;
    this.reopened = true;
    this.completed_date_local = '';
    this.original_snapshot = this.Snapshot();
    this.globalService.presentToastMessage('To Do re-opened', true);
  }

  async Close() {
    if (this.Snapshot() === this.original_snapshot && !this.client_search_query?.trim()) {
      this.modalController.dismiss(this.reopened ? { saved: true } : null);
      return;
    }
    const alert = await this.alertController.create({
      header: 'Are you sure?',
      message: 'You have unsaved changes. If you cancel now, your work will be lost.',
      buttons: [
        { text: 'Keep Editing', role: 'cancel' },
        { text: 'Discard', role: 'destructive', handler: () => this.modalController.dismiss(this.reopened ? { saved: true } : null) }
      ]
    });
    await alert.present();
  }

  private Snapshot(): string {
    return JSON.stringify([this.item, this.received_date_local, this.due_date_local, this.completed_date_local]);
  }
}
